import "../styles/css/admin-style.css"
import React, {useContext, useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {AuthenticationHandler} from "../components/auth/AuthenticationHandler";
import {publicCustomerService} from "../services/publicCustomerService";
import {Controller, useForm} from "react-hook-form";
import {useAcceptJS} from "../shared/UseAcceptJS";
import notificationService from "../services/NotificationService";
import {formatCardNumber, getBusinessOrCustomerName, getCardType, validateCardNumber} from "../shared/sharedFunctions";
import {authorize_net_client_key, authorize_net_login_id} from "../services/common.services";
import CustomInput from "../shared/components/CustomInput";
import EnlargeMapModal from "../components/PublicJobSchedule/EnlargeMapModal";
import DatePicker from "react-datepicker";
import {AvailableDatePicker} from "../shared/components/AvailableDatePicker";
import FormSelectCustom from "../shared/components/FormSelectCustom";
import {allStates, states} from "../shared/StatesDropdown";


const EstimateSchedulingPage: React.FC = () => {
    const auth = useContext(AuthenticationHandler);
    const {state} = useLocation();
    const navigate = useNavigate()
    const { register, setValue, control, getValues, trigger, formState: {errors} } = useForm();
    const [map64, setMap64] = useState('')
    const [showMapModal, setShowMapModal] = useState(false)
    const [noDate, setNoDate] = useState(false)
    const [availableDates, setAvailableDates] = useState([])
    const [billingAddressSameAsProperty, setBillingAddressSameAsProperty] = useState(true);
    const [customerData, setCustomerData] = useState({
        firstName: '',
        lastName: '',
        standardizedAddress: '',
        homePhone: '',
        email: '',
        estimatedPriceLessPatchwork: 0,
        totalJobPrice: 0,
        priceAdjustments: [],
        webDiscountAmount: -0,
        patchworkPrice: 0,
        jobId: 0,
        streetNumber: '',
        streetPrefix: '',
        streetName: '',
        streetSuffix: '',
        streetPostDirection: '',
        city: '',
        state: '',
        postalCode: ''
    });
    function getMapBase64String() {
        if(map64 && map64 !== '')
            return `data:image/png;base64,${map64}`;
        return ''
    }
    useEffect(() => {
        document.body.classList.add('bg-white');
        if(state.requestData){
            const requestData = state.requestData;
            if(!requestData.homePhone){
                if(requestData.mobilePhone){
                    requestData.homePhone = requestData.mobilePhone
                } else if(requestData.workPhone){
                    requestData.homePhone = requestData.workPhone
                }
            }
            setMap64(requestData.imageDataBase64)
            setCustomerData(requestData)
            publicCustomerService.getAvailableDates(auth.webIdUniqueToken, requestData.jobId).then(r => {
                setAvailableDates(r.availableDates)
            })
        }
    },[])

    const { loaded, error, accept } = useAcceptJS();
    const [submitting, setSubmitting] = useState(false);

    function onTokenReceived(dataValue, dataDescriptor) {
        const all = getValues()
        const parts = all.cardExp.split('/');
        if (parts.length !== 2) {
           notificationService.error("Invalid expiration format. Expected MM/YY.");
           return;
        }
        const [month, year] = parts;
        let normalizedYear = year;
        if (year.length === 2) {
            normalizedYear = `20${year}`;
        }
        const postObject = {
            preferred: true,
            paymentTokenDescriptor: dataDescriptor,
            paymentToken: dataValue,
            cardLast4: all.cardNumber.replace(/\s+/g, "").slice(-4),
            cardType: getCardType(all.cardNumber),
            cardExpirationMonth: month,
            cardExpirationYear: normalizedYear,
            billingAddress: billingAddressSameAsProperty
                ? [customerData.streetNumber, customerData.streetPrefix, customerData.streetName, customerData.streetSuffix, customerData.streetPostDirection].filter(Boolean).join(' ')
                : all.billingAddress,
            billingCity: billingAddressSameAsProperty ? customerData.city : all.billingCity,
            billingState: billingAddressSameAsProperty ? customerData.state : all.billingState,
            billingPostalCode: billingAddressSameAsProperty ? customerData.postalCode : all.postalCode,
            billingName: all.cardName,
        }
        Promise.resolve(publicCustomerService.uploadPaymentMethod(auth.webIdUniqueToken, postObject)).then(r => {
            setSubmitting(false);
            if(r){
                console.log('payment was succesfully saved')
                Promise.resolve(publicCustomerService.scheduleJob(auth.webIdUniqueToken, customerData.jobId, all.scheduleDate, all.webComments)).then(r => {
                    if(!r) return;
                    navigate('thanks');
                })
            }
        })

    }

    const handleExpDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, '').slice(0, 4); // Remove non-digits
        if (val.length >= 3) val = val.slice(0, 2) + '/' + val.slice(2);
        setValue("cardExp", val);
    };

    const handleSubmit = async () => {
        if (!loaded) {
            console.log("Payment library still loading");
            return;
        }
        if (error) {
            console.log("Payment library failed to load.");
            return;
        }
        if (!accept) {
            console.log("Accept.js not available on window.Accept.");
            return;
        }

        const all = getValues()
        const validation = await trigger();
        if(!all.scheduleDate)
            setNoDate(true)
        if (!validation || !all.scheduleDate) {
            notificationService.warning("Missing Required Fields.");
            return;
        }
        const cardNumber = all.cardNumber.replace(/\s+/g, "");
        const cardCode = all.cardSecurity.replace(/\s+/g, "");
        const name = all.cardName.trim();
        const zip = billingAddressSameAsProperty ? customerData.postalCode : all.postalCode.trim();
        const scheduleDate = all.scheduleDate
        
        setSubmitting(true);
        const parts = all.cardExp.split('/');
        if (parts.length !== 2) {
            notificationService.error("Invalid expiration format. Expected MM/YY.");
            return;
        }
        const [month, year] = parts;
        let normalizedYear = year;
        if (year.length === 2) {
            normalizedYear = `20${year}`;
        }

        const authData = {
            clientKey: authorize_net_client_key,
            apiLoginID: authorize_net_login_id,
        };
        const cardData = { cardNumber, month, year: normalizedYear, cardCode };
        const billing = { fullName: name, zip };

        const secureData = { authData, cardData, billing };

        const callback = (response: any) => {

            if (response.messages.resultCode === "Error") {
                const msg = response.messages.message[0];
                console.log(`${msg.code}: ${msg.text}`);
                notificationService.error(msg.text)
            } else {
                const { dataValue, dataDescriptor } = response.opaqueData;
                onTokenReceived(dataValue, dataDescriptor);
            }
        };

        accept.dispatchData(secureData, callback);
    };
    return (
        <>
            <div className="view-estimate-container">
                <div className="estimate-form">
                    <div className="mb-4">
                        <img src="/logo/logoBigger.svg" alt="Barreca Blacktopping" className="logo"/>
                    </div>
                    <div className="card p-4 mb-3">
                        <h3 className="mb-4 fw-bold"><i className="far fa-map-location-dot text-icon me-1"></i> Property &
                            Area Information</h3>
                        <div className="row gx-5">
                            <div className="col-md-auto mb-3 mb-md-0 text-end">
                                <div className="map-thumbnail mb-2">
                                    <img alt='map' src={getMapBase64String()}/>
                                </div>
                                <a onClick={() => setShowMapModal(true)} role="button" data-bs-toggle="modal" data-bs-target="#map-modal">enlarge map <i
                                    className="far fa-external-link"></i></a>
                            </div>
                            <div className="col-md">
                                <div className="mb-2">
                                    <h3 className="mb-0">{getBusinessOrCustomerName(customerData)}</h3>
                                    <label className="light-label">contact name</label>
                                </div>
                                <div className="mb-2">
                                    <h3 className="mb-0">{customerData.standardizedAddress}</h3>
                                    <label className="light-label">property</label>
                                </div>
                                <div className="mb-2">
                                    <h3 className="mb-0">{customerData.homePhone}</h3>
                                    <label className="light-label">phone</label>
                                </div>
                                <div>
                                    <h3 className="mb-0">{customerData.email}</h3>
                                    <label className="light-label">email</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card p-4 mb-3">
                        <h3 className="mb-4 fw-bold"><i className="far fa-calendar text-icon me-1"></i> Schedule Start Date
                        </h3>
                        <div className="row">
                            <div className={'col-lg mb-3 mb-lg-0'}>
                                <AvailableDatePicker
                                    name="scheduleDate"
                                    label="Select Date"
                                    required
                                    control={control}
                                    errors={errors}
                                    availableDates={availableDates.map(dateStr => {
                                        const [year, month, day] = dateStr.split("-").map(Number);
                                        return new Date(year, month - 1, day); // Local midnight
                                    })}
                                    className={'form-control form-control-lg'}
                                />
                                {noDate && <div className="invalid-feedback">
                                    This field is required.
                                </div>}
                            </div>

                            <div className="col-lg">
                            <label>Optional Comments</label>
                                <textarea {...register('webComments')} className="form-control form-control-lg" rows={5}></textarea>
                            </div>
                        </div>
                    </div>
                    <div className="card p-4 mb-3">
                        <h3 className="mb-4 fw-bold"><i className="far fa-credit-card text-icon me-1"></i> Payment
                            Information</h3>
                        <div className="row mb-4">
                            <div className="col-lg-8 mb-3 mb-lg-0">
                                <CustomInput label={'Card Number'} required name={'cardNumber'}
                                             className={'form-control form-control-lg'} register={register}
                                             errors={errors}
                                             onChange={(e) => {
                                                 const raw = e.target.value;
                                                 const formatted = formatCardNumber(raw);
                                                 setValue("cardNumber", formatted);
                                             }}
                                             validation={{
                                                 validate: (value: string) =>
                                                     validateCardNumber(value) || "Invalid card number"
                                             }}/>
                            </div>
                            <div className="col-lg mb-3 mb-lg-0">
                                <CustomInput label={'Exp'} required name={'cardExp'} onChange={handleExpDateChange}
                                             className={'form-control form-control-lg'} register={register}
                                             errors={errors}/>

                            </div>
                            <div className="col-lg">
                                <CustomInput label={'Security Code'} required name={'cardSecurity'}
                                             className={'form-control form-control-lg'} register={register}
                                             errors={errors} type={'number'}/>

                            </div>
                        </div>
                        <div className="row">
                            <div className={billingAddressSameAsProperty ? "col-lg-12 mb-3 mb-lg-0" : "col-lg-9 mb-3 mb-lg-0"}>
                                <CustomInput label={'Name on Card'} required name={'cardName'}
                                             className={'form-control form-control-lg'} register={register}
                                             errors={errors}/>

                            </div>
                            {!billingAddressSameAsProperty && (
                                <div className="col-lg">
                                    <CustomInput label={'Zip Code'} required name={'postalCode'}
                                                 className={'form-control form-control-lg'} register={register}
                                                 errors={errors} type={'number'}/>
                                </div>
                            )}
                        </div>
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="form-check">
                                    <input className="form-check-input" type="checkbox" id="billingSameAsProperty"
                                           checked={billingAddressSameAsProperty}
                                           onChange={(e) => setBillingAddressSameAsProperty(e.target.checked)}/>
                                    <label className="form-check-label" htmlFor="billingSameAsProperty">
                                        Billing address same as property address
                                    </label>
                                </div>
                            </div>
                        </div>
                        {!billingAddressSameAsProperty && (
                            <div className="row">
                                <div className="col-lg-6 mb-3 mb-lg-0">
                                    <CustomInput label={'Billing Address'} required name={'billingAddress'}
                                                 className={'form-control form-control-lg'} register={register}
                                                 errors={errors}/>

                                </div>
                                <div className="col-lg-4">
                                    <CustomInput label={'City'} required name={'billingCity'}
                                                 className={'form-control form-control-lg'} register={register}
                                                 errors={errors}/>
                                </div>
                                <div className="col-lg-2">
                                    <FormSelectCustom
                                        label='State'
                                        name="billingState"
                                        className={'form-control form-control-lg'}
                                        register={register}
                                        options={allStates}
                                        required
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="estimate-sidebar">
                    <div className="esimate-cost p-3">
                        <label>Your estimate</label>
                        <h2>${customerData.totalJobPrice}</h2>
                    </div>
                    <hr/>
                    <div className="p-3">
                        <h3 className="mb-3">Summary</h3>
                        <div className="row mb-3 align-items-center justify-content-between">
                            <div className="col-auto">Sealcoating</div>
                            <div className="col-auto text-end">${customerData.estimatedPriceLessPatchwork}</div>
                        </div>
                        {customerData.patchworkPrice > 0 &&
                            <div className="row mb-3 align-items-center justify-content-between">
                                <div className="col-auto">Patchwork</div>
                                <div className="col-auto text-end">${customerData.patchworkPrice}</div>
                            </div>}
                        <div className="row align-items-center justify-content-between">
                            <div className="col-auto">Subtotal</div>
                            <div
                                className="col-auto text-end">${customerData.estimatedPriceLessPatchwork + customerData.patchworkPrice}</div>
                        </div>
                    </div>
                    <hr/>
                    {customerData.priceAdjustments.map(x => {
                        return (
                            <div className="row align-items-center justify-content-between">
                                <div className="col-auto">{x.adjustmentDescription}</div>
                                <div className="col-auto text-end text-success fw-bold">{x.adjustmentAmount}</div>
                            </div>
                        )
                    })}
                    <div className="row align-items-center justify-content-between">
                        <div className="col-auto">Web Discount</div>
                        <div className="col-auto text-end text-success fw-bold">{customerData.webDiscountAmount}</div>
                    </div>
                    <hr/>
                    <div className="row align-items-center justify-content-between mb-5">
                        <div className="col-auto">Total</div>
                        <div className="col-auto text-end text-primary fw-bold">${customerData.totalJobPrice}</div>
                    </div>
                    <button disabled={submitting} onClick={handleSubmit} type="button"
                            className="btn btn-primary btn-lg w-100 fw-bold py-4 mb-5"><i
                        className="far fa-check"></i> Schedule Work
                    </button>
                    <div className="row">
                        <div className="col-auto">
                            <i className="fas fa-question-circle fa-2x" style={{color: "#C1CBCF"}}></i>
                        </div>
                        <div className="col">
                            <p className="mb-0">Please contact Barreca with any questions or concerns.</p>
                        </div>
                    </div>
                </div>
            </div>
            <EnlargeMapModal base64String={map64} show={showMapModal} toggle={() => setShowMapModal(false)}/>
        </>
    )
}

export default EstimateSchedulingPage